import { useEffect, useState } from "react";

type Env = { env: string; commit: string };

/**
 * A thin bar on every page of an environment that is NOT prod:
 * "DEV · <commit>". The api says which environment it is on /api/health
 * (`env`, `commit`; docs/infra/dev-prod-plan.md §8). Prod, and an api from
 * before those fields, show nothing. The same bundle runs in dev and prod,
 * so the bar can only come from the server's answer, never from the build.
 */
export function EnvBanner() {
  const [env, setEnv] = useState<Env | null>(null);

  useEffect(() => {
    const ctl = new AbortController();
    fetch("/api/health", { signal: ctl.signal, credentials: "same-origin" })
      .then((r) => (r.ok ? r.json() : null))
      .then((body: { env?: unknown; commit?: unknown } | null) => {
        if (body && typeof body.env === "string" && body.env !== "prod") {
          setEnv({ env: body.env, commit: typeof body.commit === "string" ? body.commit : "" });
        }
      })
      .catch(() => undefined);
    return () => ctl.abort();
  }, []);

  if (!env) return null;
  return (
    <div
      role="status"
      className="pointer-events-none fixed inset-x-0 top-0 z-[130] flex justify-center pt-[env(safe-area-inset-top)]"
    >
      <span className="rounded-b-md border border-t-0 border-warning/40 bg-warning/15 px-3 py-0.5 text-xs font-semibold uppercase tracking-wide text-warning">
        {env.env}
        {env.commit ? ` · ${env.commit.slice(0, 8)}` : ""}
      </span>
    </div>
  );
}
